import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, FileText, Sparkles, CheckCircle, Circle, ChevronDown, ChevronUp,
  Download, Printer, RefreshCw, Loader2, AlertCircle, Table, Brain, Copy, Check, AlertTriangle
} from 'lucide-react';
import {
  Table as UITable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import AIFeedbackPopup, { shouldShowFeedback } from '@/components/AIFeedbackPopup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Report sections configuration - mapped to clinical_area values from AssessmentModule
const REPORT_SECTIONS = [
  { 
    id: 'header_history', 
    name: 'Header & Case History',
    description: 'Client information, background, developmental history',
    dataFields: ['reason_for_referral', 'background_info', 'medical_history', 'developmental_history'],
    noAIInterpretation: true // Just format entered data, don't interpret
  },
  { 
    id: 'attention_behaviour', 
    name: 'Attention & Behaviour',
    description: 'Observations during assessment sessions',
    dataFields: ['attention_observations', 'behaviour_observations'],
    noAIInterpretation: true // Just format entered data, don't interpret
  },
  { 
    id: 'prelinguistic', 
    name: 'Prelinguistic Skills',
    description: 'Eye contact, joint attention, turn-taking, imitation',
    dataFields: ['prelinguistic_observations'],
    clinicalAreaId: 'prelinguistic', // Maps to clinical_area from AssessmentModule
    nonStandardizedKeywords: ['prelinguistic', 'joint attention', 'eye contact', 'turn-taking', 'imitation', 'gestures', 'intentional communication']
  },
  { 
    id: 'receptive_language', 
    name: 'Receptive Language',
    description: 'Language comprehension and understanding',
    dataFields: ['receptive_observations'],
    hasScores: true,
    clinicalAreaId: 'receptive', // Maps to clinical_area from AssessmentModule
    // Tests that go here: receptive vocab, auditory comprehension tests
    scoreFilter: ['TACL', 'PPVT', 'ROWPVT', 'REEL', 'CASL'],
    // Tests with specific subtests that go here
    subtestFilters: [
      { test: 'CELF', subtests: ['Receptive', 'Comprehension', 'Concepts', 'Following Directions', 'Sentence Comprehension', 'Word Classes-Receptive', 'Understanding'] },
      { test: 'PLS', subtests: ['Auditory Comprehension', 'Receptive'] },
      { test: 'WABC', subtests: ['Receptive', 'Basic Concepts'] },
      { test: 'TOLD', subtests: ['Listening', 'Receptive'] },
      { test: 'OWLS', subtests: ['Listening Comprehension', 'Receptive'] }
    ],
    nonStandardizedKeywords: ['receptive', 'comprehension', 'understanding', 'following directions', 'vocabulary comprehension']
  },
  { 
    id: 'auditory_processing', 
    name: 'Auditory Processing',
    description: 'Auditory discrimination, memory, sequencing',
    dataFields: ['auditory_discrimination', 'auditory_memory', 'auditory_sequencing', 'listening_comprehension', 'auditory_notes'],
    hasScores: true,
    clinicalAreaId: 'auditory', // Maps to clinical_area from AssessmentModule
    scoreFilter: ['TAPS', 'SCAN'],
    // Non-standardized measure keywords to match
    nonStandardizedKeywords: ['auditory', 'listening', 'processing']
  },
  { 
    id: 'expressive_language', 
    name: 'Expressive Language',
    description: 'Language output, GLP stage analysis, language sample',
    dataFields: ['expressive_observations', 'language_sample_analysis'],
    hasScores: true,
    hasLanguageSample: true, // Special flag for language sample analysis
    clinicalAreaId: 'expressive', // Maps to clinical_area from AssessmentModule
    // Tests that go here: expressive vocab, expressive language tests
    scoreFilter: ['EOWPVT', 'EVT', 'TEXL', 'SPELT', 'RAPT', 'BUS-STORY'],
    // Tests with specific subtests that go here
    subtestFilters: [
      { test: 'CELF', subtests: ['Expressive', 'Formulating', 'Recalling', 'Word Structure', 'Sentence Assembly', 'Word Classes-Expressive'] },
      { test: 'PLS', subtests: ['Expressive Communication', 'Expressive'] },
      { test: 'WABC', subtests: ['Expressive'] },
      { test: 'TOLD', subtests: ['Speaking', 'Expressive', 'Organizing'] },
      { test: 'OWLS', subtests: ['Oral Expression', 'Expressive'] }
    ],
    nonStandardizedKeywords: ['expressive', 'expression', 'language sample', 'utterance', 'MLU', 'sentence', 'vocabulary use', 'word finding']
  },
  { 
    id: 'literacy', 
    name: 'Literacy & Phonological Awareness',
    description: 'Reading, writing, phonological processing skills',
    dataFields: ['literacy_observations', 'phonological_awareness_notes', 'reading_notes', 'spelling_notes'],
    hasScores: true,
    clinicalAreaId: 'literacy', // Maps to clinical_area from AssessmentModule
    scoreFilter: ['CTOPP', 'TOWRE', 'GORT', 'PAT', 'WRMT', 'WRAT', 'QRI', 'DIBELS'],
    // Non-standardized measure keywords to match
    nonStandardizedKeywords: ['literacy', 'reading', 'spelling', 'phonological', 'phonemic', 'decoding', 'writing']
  },
  {
    id: 'pragmatics',
    name: 'Pragmatic Language & Social Communication',
    description: 'Social use of language, conversation skills',
    dataFields: ['pragmatic_observations'],
    hasScores: true,
    scoreFilter: ['CCC', 'TOPL', 'CASL'],
    subtestFilters: [
      { test: 'CELF', subtests: ['Pragmatic'] },
      { test: 'CASL', subtests: ['Pragmatic'] }
    ],
    // Specific pragmatic language keywords
    nonStandardizedKeywords: ['pragmatic language', 'social communication', 'conversation skill', 'topic maintenance', 'turn taking', 'nonverbal communication', 'inference skill']
  },
  { 
    id: 'social_emotional', 
    name: 'Social-Emotional Skills',
    description: 'Self-advocacy, emotional regulation, peer interactions',
    dataFields: ['social_emotional_observations'],
    clinicalAreaId: 'social_emotional', // Maps to clinical_area from AssessmentModule
    // More specific keywords to avoid overlap
    nonStandardizedKeywords: ['self-advocacy skill', 'peer interaction', 'friendship skill', 'anxiety management', 'emotional regulation', 'coping strategy', 'emotional awareness', 'self-regulation']
  },
  {
    id: 'aac_assessment',
    name: 'AAC Assessment',
    description: 'Augmentative and alternative communication profile',
    dataFields: ['aac_system_type', 'aac_device_name', 'aac_access_method', 'aac_vocabulary_type', 'aac_grid_size', 'aac_current_level', 'aac_barriers', 'aac_supports_needed', 'aac_observations'],
    clinicalAreaId: 'aac', // Maps to clinical_area from AssessmentModule
    nonStandardizedKeywords: ['AAC', 'augmentative', 'alternative', 'device', 'communication board', 'PECS', 'SGD', 'aided']
  },
  { 
    id: 'speech_motor', 
    name: 'Speech & Oral Motor',
    description: 'Articulation, motor planning, PROMPT hierarchy',
    dataFields: ['speech_observations', 'articulation_notes'],
    hasScores: true,
    clinicalAreaId: 'speech_motor', // Maps to clinical_area from AssessmentModule
    scoreFilter: ['GFTA', 'HAPP', 'DEAP', 'NUFFIELD', 'KLPA', 'SSI'],
    // More specific keywords to avoid matching unrelated content
    nonStandardizedKeywords: ['speech sound', 'articulation', 'phonology pattern', 'oral motor', 'apraxia', 'dysarthria', 'intelligibility', 'stimulability', 'phonological process']
  },
  {
    id: 'prompt_hierarchy',
    name: 'PROMPT Analysis',
    description: 'Motor speech hierarchy assessment',
    dataFields: ['prompt_jaw', 'prompt_lip', 'prompt_facial', 'prompt_lingual', 'prompt_sequencing', 'prompt_level', 'prompt_cueing', 'prompt_observations'],
    parentSection: 'speech_motor',
    clinicalAreaId: 'speech_motor', // PROMPT is part of speech_motor clinical area
    // Very specific keywords to avoid false positives
    nonStandardizedKeywords: ['PROMPT technique', 'PROMPT hierarchy', 'motor speech', 'tactile cue', 'jaw control', 'lip rounding']
  },
  { 
    id: 'summary_recommendations', 
    name: 'Summary & Recommendations',
    description: 'Key findings and actionable recommendations',
    dataFields: ['clinical_impressions', 'diagnosis', 'recommendations', 'goals']
  },
  // Catch-all: any score in assessment.standardized_scores that didn't map
  // to a specific section shows up here so nothing is silently dropped.
  // This section is auto-populated by `unassignedScoresList` in the
  // component; noAIInterpretation because it's a table of raw results.
  {
    id: 'additional_test_results',
    name: 'Additional Test Results',
    description: 'Test scores from the assessment tracker not yet mapped to a specific section',
    hasScores: true,
    noAIInterpretation: false, // AI still writes a paragraph describing them
    catchAll: true, // marker used by getScoresForSection
  }
];

// Auto-format text into professional paragraphs (non-AI)
const autoFormatText = (text, fieldName) => {
  if (!text || text.trim() === '') return null;
  
  // Clean up the text
  let formatted = text.trim();
  
  // Convert bullet points to sentences if present
  if (formatted.includes('•') || formatted.includes('-') || formatted.includes('*')) {
    const lines = formatted.split(/\n/).filter(l => l.trim());
    const sentences = lines.map(line => {
      let cleaned = line.replace(/^[\s•\-\*]+/, '').trim();
      if (cleaned && !cleaned.endsWith('.') && !cleaned.endsWith('!') && !cleaned.endsWith('?')) {
        cleaned += '.';
      }
      return cleaned;
    }).filter(s => s.length > 1);
    formatted = sentences.join(' ');
  }
  
  // Ensure proper sentence endings
  if (formatted && !formatted.endsWith('.') && !formatted.endsWith('!') && !formatted.endsWith('?')) {
    formatted += '.';
  }
  
  // Capitalize first letter
  if (formatted) {
    formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }
  
  return formatted;
};

// Generate score interpretation text
// Interpret scores based on score type
// Standard scores: mean=100, SD=15 (typical ranges: 85-115 average)
// Scaled scores: mean=10, SD=3 (typical ranges: 7-13 average)
const getScoreInterpretation = (score, scoreType = 'standard') => {
  if (score === null || score === undefined) return 'N/A';
  const numScore = parseInt(score);
  if (isNaN(numScore)) return 'N/A';
  
  if (scoreType === 'scaled') {
    // Scaled score interpretation (mean=10, SD=3)
    if (numScore >= 13) return 'Above Average';
    if (numScore >= 7) return 'Average';
    if (numScore >= 4) return 'Below Average';
    if (numScore >= 1) return 'Low';
    return 'Very Low';
  }
  
  // Standard score interpretation (mean=100, SD=15)
  if (numScore >= 115) return 'Above Average';
  if (numScore >= 85) return 'Average';
  if (numScore >= 70) return 'Below Average';
  if (numScore >= 55) return 'Low';
  return 'Very Low';
};

export default function HybridReportGenerator({ onLogout }) {
  const { childId, assessmentId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [chunks, setChunks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSections, setSelectedSections] = useState({});
  const [sectionInterpretations, setSectionInterpretations] = useState({});
  const [generatingSection, setGeneratingSection] = useState(null);
  const [feedbackSection, setFeedbackSection] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [letterhead, setLetterhead] = useState(null);
  const [syncingScores, setSyncingScores] = useState(false);
  // Inline edit state for section interpretations — { [sectionId]: draftText }
  // If a section id is present here we render a textarea instead of the
  // read-only paragraph. Save writes back to sectionInterpretations + DB.
  const [editingSection, setEditingSection] = useState(null);
  const [editDraft, setEditDraft] = useState('');

  useEffect(() => {
    fetchData();
  }, [childId, assessmentId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [childRes, assessmentRes, chunksRes, letterheadRes] = await Promise.all([
        axios.get(`${API}/children/${childId}`, { headers }),
        axios.get(`${API}/assessments/detail/${assessmentId}`, { headers }),
        axios.get(`${API}/chunks/${childId}`, { headers }),
        axios.get(`${API}/therapist/letterhead`, { headers }).catch(() => ({ data: null }))
      ]);
      
      setChild(childRes.data);
      setAssessment(assessmentRes.data);
      setChunks(chunksRes.data || []);
      setLetterhead(letterheadRes.data);
      
      // Load existing interpretations if any
      if (assessmentRes.data.section_interpretations) {
        setSectionInterpretations(assessmentRes.data.section_interpretations);
      }
      
      // Initialize section selection based on data availability
      const initialSelection = {};
      REPORT_SECTIONS.forEach(section => {
        const hasData = checkSectionHasData(assessmentRes.data, section);
        initialSelection[section.id] = hasData;
      });
      setSelectedSections(initialSelection);
      
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load assessment data');
    } finally {
      setLoading(false);
    }
  };

  // Get non-standardized measures that match a section's keywords
  // Get non-standardized measures for a section
  // Priority: 1) Match by clinical_area field, 2) Fallback to keyword matching
  const getNonStandardizedForSection = (assessmentData, section) => {
    if (!assessmentData?.non_standardized_measures) {
      return [];
    }
    
    const measures = assessmentData.non_standardized_measures;
    const matchedMeasures = [];
    const matchedIds = new Set();
    
    // First, match by clinical_area if section has clinicalAreaId
    if (section.clinicalAreaId) {
      measures.forEach(measure => {
        if (measure.clinical_area === section.clinicalAreaId) {
          matchedMeasures.push(measure);
          matchedIds.add(measure.title + measure.findings); // Simple unique key
        }
      });
    }
    
    // Then, match by keywords for measures without a clinical_area assigned
    // or if the section doesn't have a clinicalAreaId
    if (section.nonStandardizedKeywords) {
      const keywords = section.nonStandardizedKeywords.map(k => k.toLowerCase());
      
      measures.forEach(measure => {
        // Skip if already matched by clinical_area
        const measureKey = measure.title + measure.findings;
        if (matchedIds.has(measureKey)) return;
        
        // Only keyword-match measures that don't have a clinical_area assigned
        // (to avoid duplicating measures across sections)
        if (measure.clinical_area && measure.clinical_area !== '') return;
        
        const titleLower = (measure.title || '').toLowerCase();
        const findingsLower = (measure.findings || '').toLowerCase();
        const typeLower = (measure.measure_type || '').toLowerCase();
        
        const matches = keywords.some(keyword => 
          titleLower.includes(keyword) || 
          findingsLower.includes(keyword) ||
          typeLower.includes(keyword)
        );
        
        if (matches) {
          matchedMeasures.push(measure);
          matchedIds.add(measureKey);
        }
      });
    }
    
    return matchedMeasures;
  };

  // Check if section has data
  const checkSectionHasData = (assessmentData, section) => {
    if (!assessmentData) return false;
    
    // Check data fields
    for (const field of section.dataFields || []) {
      const value = assessmentData[field];
      if (value && (typeof value === 'string' ? value.trim() : true)) {
        return true;
      }
    }
    
    // Check scores if applicable
    if (section.hasScores && assessmentData.standardized_scores?.length > 0) {
      // Catch-all section: has data iff at least one score DOESN'T match
      // any specific section (i.e. unassigned scores exist).
      if (section.catchAll) {
        const specificSections = REPORT_SECTIONS.filter(s => s.hasScores && !s.catchAll);
        const anyUnassigned = assessmentData.standardized_scores.some(
          score => !specificSections.some(sec => scoreMatchesSection(score, sec))
        );
        if (anyUnassigned) return true;
      } else {
        const relevantScores = assessmentData.standardized_scores.filter(s => 
          scoreMatchesSection(s, section)
        );
        if (relevantScores.length > 0) return true;
      }
    }
    
    // Check non-standardized measures (by clinical_area or keywords)
    const relevantMeasures = getNonStandardizedForSection(assessmentData, section);
    if (relevantMeasures.length > 0) return true;
    
    return false;
  };

  // Check if a score matches a section (considering clinical_area, test code and subtest name)
  const scoreMatchesSection = (score, section) => {
    const testCode = score.test_code?.toUpperCase() || '';
    const subtestName = score.subtest_name?.toLowerCase() || '';
    
    // FIRST: Check if score has a clinical_area that matches this section
    if (score.clinical_area && section.clinicalAreaId) {
      if (score.clinical_area === section.clinicalAreaId) {
        return true;
      }
    }
    
    // SECOND: Check direct scoreFilter matches
    if (section.scoreFilter?.some(filter => testCode.includes(filter.toUpperCase()))) {
      // If this section has subtestFilters, make sure the subtest matches
      if (section.subtestFilters) {
        const subtestFilter = section.subtestFilters.find(sf => 
          testCode.includes(sf.test.toUpperCase())
        );
        if (subtestFilter) {
          // Check if subtest name contains any of the allowed subtest keywords
          return subtestFilter.subtests.some(allowed => 
            subtestName.includes(allowed.toLowerCase())
          );
        }
      }
      return true;
    }
    
    // THIRD: Check subtestFilters for tests not in direct scoreFilter
    if (section.subtestFilters) {
      return section.subtestFilters.some(sf => {
        if (testCode.includes(sf.test.toUpperCase())) {
          return sf.subtests.some(allowed => 
            subtestName.includes(allowed.toLowerCase())
          );
        }
        return false;
      });
    }
    
    return false;
  };

  // Get standardized scores for a section
  const getScoresForSection = (section) => {
    if (!assessment?.standardized_scores || !section.hasScores) return [];

    // The catch-all section receives every score that DID NOT match any
    // other section — this guarantees no score is silently dropped from
    // the report just because its test code isn't in our filter list.
    if (section.catchAll) {
      const specificSections = REPORT_SECTIONS.filter(s => s.hasScores && !s.catchAll);
      return assessment.standardized_scores.filter(score =>
        !specificSections.some(sec => scoreMatchesSection(score, sec))
      );
    }

    return assessment.standardized_scores.filter(score => scoreMatchesSection(score, section));
  };

  // Get auto-formatted content for a section (including non-standardized measures)
  const getAutoFormattedContent = (section) => {
    if (!assessment) return null;
    
    const contents = [];
    
    // Special handling for AAC section - format as structured data
    if (section.id === 'aac_assessment') {
      const aacParts = [];
      
      // System overview
      if (assessment.aac_system_type && assessment.aac_system_type !== 'none') {
        const systemTypeLabels = {
          'high_tech': 'High-Tech SGD',
          'low_tech': 'Low-Tech Communication System',
          'pecs': 'PECS (Picture Exchange Communication System)',
          'hybrid': 'Hybrid System (multiple modalities)',
          'trialing': 'Currently trialing AAC options',
          'recommended': 'AAC recommended but not yet implemented'
        };
        let systemInfo = `**Current AAC System:** ${systemTypeLabels[assessment.aac_system_type] || assessment.aac_system_type}`;
        if (assessment.aac_device_name) {
          systemInfo += ` - ${assessment.aac_device_name}`;
        }
        aacParts.push(systemInfo);
      }
      
      // Access method
      if (assessment.aac_access_method) {
        const accessLabels = {
          'direct_selection': 'Direct Selection (finger/hand)',
          'eye_gaze': 'Eye Gaze',
          'switch_scanning': 'Switch Scanning',
          'head_tracking': 'Head Tracking',
          'partner_assisted': 'Partner-Assisted Selection',
          'multiple': 'Multiple Access Methods'
        };
        aacParts.push(`**Access Method:** ${accessLabels[assessment.aac_access_method] || assessment.aac_access_method}`);
      }
      
      // Vocabulary info
      if (assessment.aac_vocabulary_type || assessment.aac_grid_size) {
        const vocabLabels = {
          'core_word': 'Core Word Based',
          'fringe': 'Fringe/Topic-Based',
          'combination': 'Combination (Core + Fringe)',
          'motor_planning': 'Motor Planning Based',
          'scene_based': 'Visual Scene Based'
        };
        let vocabInfo = '';
        if (assessment.aac_vocabulary_type) {
          vocabInfo = `**Vocabulary:** ${vocabLabels[assessment.aac_vocabulary_type] || assessment.aac_vocabulary_type}`;
        }
        if (assessment.aac_grid_size) {
          vocabInfo += vocabInfo ? ` (${assessment.aac_grid_size} grid)` : `**Grid Size:** ${assessment.aac_grid_size}`;
        }
        if (vocabInfo) aacParts.push(vocabInfo);
      }
      
      // Current level
      if (assessment.aac_current_level) {
        aacParts.push(`**Current Level:** ${assessment.aac_current_level}`);
      }
      
      // Barriers
      if (assessment.aac_barriers) {
        const formatted = autoFormatText(assessment.aac_barriers, 'aac_barriers');
        if (formatted) aacParts.push(`**Barriers to AAC Use:** ${formatted}`);
      }
      
      // Supports needed
      if (assessment.aac_supports_needed) {
        const formatted = autoFormatText(assessment.aac_supports_needed, 'aac_supports_needed');
        if (formatted) aacParts.push(`**Supports Needed:** ${formatted}`);
      }
      
      // Observations
      if (assessment.aac_observations) {
        const formatted = autoFormatText(assessment.aac_observations, 'aac_observations');
        if (formatted) aacParts.push(`**AAC Observations:** ${formatted}`);
      }
      
      return aacParts.length > 0 ? aacParts.join('\n\n') : null;
    }
    
    // Check direct data fields
    for (const field of section.dataFields || []) {
      const value = assessment[field];
      if (value) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            contents.push(value.join('. ') + '.');
          }
        } else if (typeof value === 'string' && value.trim()) {
          // Special formatting for language_sample_analysis
          if (field === 'language_sample_analysis') {
            contents.push(`**Language Sample Analysis:**\n${value.trim()}`);
          } else {
            const formatted = autoFormatText(value, field);
            if (formatted) contents.push(formatted);
          }
        }
      }
    }
    
    // Check non-standardized measures that match this section (by clinical_area or keywords)
    const relevantMeasures = getNonStandardizedForSection(assessment, section);
    for (const measure of relevantMeasures) {
      if (measure.findings?.trim()) {
        const measureType = measure.measure_type ? ` (${measure.measure_type})` : '';
        const header = measure.title ? `**${measure.title}${measureType}:**` : '';
        const formatted = autoFormatText(measure.findings, 'findings');
        if (formatted) {
          contents.push(header ? `${header}\n${formatted}` : formatted);
        }
      }
    }
    
    return contents.length > 0 ? contents.join('\n\n') : null;
  };

  // Copy section content to clipboard
  const [copiedSection, setCopiedSection] = useState(null);
  const [copiedTable, setCopiedTable] = useState(null);
  
  // Copy scores table to clipboard in a format that pastes well into documents
  const copyScoresTable = async (scores, sectionName) => {
    // Determine which optional columns have data
    const hasPercentile = scores.some(s => s.percentile_rank != null && s.percentile_rank !== '');
    const hasAgeEquiv = scores.some(s => s.age_equivalent != null && s.age_equivalent !== '');
    const hasRawScore = scores.some(s => s.raw_score != null && s.raw_score !== '');
    
    // Build dynamic headers - only include columns with data
    const headers = ['Test', 'Subtest', 'Score Type', 'Score'];
    if (hasPercentile) headers.push('Percentile');
    if (hasAgeEquiv) headers.push('Age Equivalent');
    if (hasRawScore) headers.push('Raw Score');
    headers.push('Interpretation');
    
    // Build rows with matching columns
    const rows = scores.map(score => {
      const scoreType = score.score_type || 'standard';
      const displayScore = score.converted_score ?? score.standard_score ?? score.scaled_score;
      const interpretation = getScoreInterpretation(displayScore, scoreType);
      
      const row = [
        score.test_code || '',
        score.subtest_name || '-',
        scoreType === 'scaled' ? 'Scaled' : 'Standard',
        displayScore ?? '-'
      ];
      if (hasPercentile) row.push(score.percentile_rank ?? '-');
      if (hasAgeEquiv) row.push(score.age_equivalent ?? '-');
      if (hasRawScore) row.push(score.raw_score ?? '-');
      row.push(interpretation);
      
      return row;
    });
    
    // Tab-separated text for plain text paste
    const tableText = [headers.join('\t'), ...rows.map(r => r.join('\t'))].join('\n');
    
    // HTML table for Word compatibility
    const htmlTable = `
      <table style="border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; font-size: 10pt;">
        <thead>
          <tr style="background-color: #f5f5f5;">
            ${headers.map(h => `<th style="border: 1px solid #666; padding: 6px 8px; text-align: left; font-weight: bold;">${h}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${rows.map(row => `
            <tr>
              ${row.map(cell => `<td style="border: 1px solid #666; padding: 6px 8px;">${cell}</td>`).join('')}
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;
    
    try {
      // Try to copy HTML for better Word compatibility
      if (navigator.clipboard.write) {
        try {
          const htmlBlob = new Blob([htmlTable], { type: 'text/html' });
          const textBlob = new Blob([tableText], { type: 'text/plain' });
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': htmlBlob,
              'text/plain': textBlob
            })
          ]);
          setCopiedTable(sectionName);
          toast.success('Table copied! Paste into Word for formatted table');
          setTimeout(() => setCopiedTable(null), 2000);
        } catch (htmlErr) {
          // Fallback to plain text
          await navigator.clipboard.writeText(tableText);
          setCopiedTable(sectionName);
          toast.success('Table copied! Paste into Word, Docs, or Excel');
          setTimeout(() => setCopiedTable(null), 2000);
        }
      } else {
        await navigator.clipboard.writeText(tableText);
        setCopiedTable(sectionName);
        toast.success('Table copied! Paste into Word, Docs, or Excel');
        setTimeout(() => setCopiedTable(null), 2000);
      }
    } catch (err) {
      toast.error('Failed to copy table');
    }
  };
  
  const copySectionContent = async (section) => {
    const parts = [];
    
    // Add section title
    parts.push(`## ${section.name}\n`);
    
    // Add scores as formatted table
    const scores = getScoresForSection(section);
    if (scores.length > 0) {
      parts.push('### Standardized Assessment Results\n');
      
      // Determine which optional columns have data
      const hasPercentile = scores.some(s => s.percentile_rank != null && s.percentile_rank !== '');
      const hasAgeEquiv = scores.some(s => s.age_equivalent != null && s.age_equivalent !== '');
      const hasRawScore = scores.some(s => s.raw_score != null && s.raw_score !== '');
      
      // Build dynamic headers
      const headers = ['Test', 'Subtest', 'Score Type', 'Score'];
      if (hasPercentile) headers.push('Percentile');
      if (hasAgeEquiv) headers.push('Age Equivalent');
      if (hasRawScore) headers.push('Raw Score');
      headers.push('Interpretation');
      
      parts.push(headers.join('\t'));
      
      scores.forEach(score => {
        const scoreType = score.score_type || 'standard';
        const displayScore = score.converted_score ?? score.standard_score ?? score.scaled_score;
        const interpretation = getScoreInterpretation(displayScore, scoreType);
        const scoreLabel = scoreType === 'scaled' ? 'Scaled' : 'Standard';
        
        const row = [
          score.test_code || '',
          score.subtest_name || '-',
          scoreLabel,
          displayScore ?? '-'
        ];
        if (hasPercentile) row.push(score.percentile_rank ?? '-');
        if (hasAgeEquiv) row.push(score.age_equivalent ?? '-');
        if (hasRawScore) row.push(score.raw_score ?? '-');
        row.push(interpretation);
        
        parts.push(row.join('\t'));
      });
      parts.push('');
    }
    
    // Add auto-formatted content
    const autoContent = getAutoFormattedContent(section);
    if (autoContent) {
      parts.push('### Observations\n');
      parts.push(autoContent);
      parts.push('');
    }
    
    // Add interpretation if present
    const interpretation = sectionInterpretations[section.id];
    if (interpretation) {
      parts.push('### Clinical Interpretation\n');
      parts.push(interpretation);
    }
    
    const textToCopy = parts.join('\n');
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopiedSection(section.id);
      toast.success(`${section.name} copied to clipboard!`);
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  // Generate AI interpretation for a section
  const generateInterpretation = async (sectionId, options = {}) => {
    const { synthesiseFromPrevious = false } = options;
    setGeneratingSection(sectionId);
    
    try {
      const token = localStorage.getItem('token');
      const section = REPORT_SECTIONS.find(s => s.id === sectionId);
      
      // Build chunk samples text for relevant sections
      let chunkSamplesText = '';
      if (['expressive_language', 'receptive_language', 'prelinguistic', 'social_emotional'].includes(sectionId) && chunks.length > 0) {
        const recentChunks = chunks.slice(0, 20);
        chunkSamplesText = recentChunks.map(c => `"${c.text}" (${c.context || 'no context'})`).join('\n');
      }
      
      // Gather section data with language learner profile
      const sectionData = {
        autoContent: getAutoFormattedContent(section),
        scores: getScoresForSection(section),
        childName: child?.name,
        childAge: child?.age,
        communicationStyle: child?.communication_style,
        languageProfile: assessment?.language_learner_profile || 'Not specified',
        languageProfileOther: assessment?.language_learner_profile_other || '',
        chunkSamples: chunkSamplesText
      };
      
      const response = await axios.post(
        `${API}/reports/interpret-section`,
        {
          assessment_id: assessmentId,
          section_id: sectionId,
          section_name: section.name,
          section_data: sectionData,
          format_only: section.noAIInterpretation || false, // Don't interpret, just format
          synthesise_from_previous: synthesiseFromPrevious
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000
        }
      );
      
      if (response.data.interpretation) {
        setSectionInterpretations(prev => ({
          ...prev,
          [sectionId]: response.data.interpretation
        }));
        toast.success(
          synthesiseFromPrevious
            ? `${section.name} synthesised from your report findings!`
            : `${section.name} interpretation generated!`
        );
        if (shouldShowFeedback('report_interpretation')) {
          setFeedbackSection(sectionId);
        }
        
        // Save to assessment
        await axios.put(
          `${API}/assessments/${assessmentId}`,
          {
            section_interpretations: {
              ...sectionInterpretations,
              [sectionId]: response.data.interpretation
            }
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (error) {
      console.error('Failed to generate interpretation:', error);
      toast.error('Failed to generate interpretation. Please try again.');
    } finally {
      setGeneratingSection(null);
    }
  };

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Sync scores from the linked assessment tracker into this assessment,
  // merging without dropping already-added scores.
  const syncFromTracker = async () => {
    if (syncingScores) return;
    setSyncingScores(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/assessments/${assessmentId}/sync-from-tracker`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { added, updated, total_scores } = res.data;
      if (added === 0 && updated === 0) {
        toast.info('All tracker scores are already in this report.');
      } else {
        toast.success(`Synced from tracker — ${added} new score${added === 1 ? '' : 's'} added, ${updated} updated. ${total_scores} total.`);
      }
      // Refetch to pick up the new scores
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not sync from tracker');
    } finally {
      setSyncingScores(false);
    }
  };

  // Start editing a section interpretation in place.
  const startEditSection = (sectionId) => {
    setEditingSection(sectionId);
    setEditDraft(sectionInterpretations[sectionId] || '');
  };

  // Save the inline edit — updates local state + persists to the assessment.
  const saveEditSection = async () => {
    if (!editingSection) return;
    const sectionId = editingSection;
    const nextText = editDraft;
    const next = { ...sectionInterpretations, [sectionId]: nextText };
    setSectionInterpretations(next);
    setEditingSection(null);
    setEditDraft('');
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/assessments/${assessmentId}`,
        { section_interpretations: next },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Section saved');
    } catch (e) {
      toast.error('Could not save — please try again');
    }
  };

  const cancelEditSection = () => {
    setEditingSection(null);
    setEditDraft('');
  };

  // Export/Print report
  const exportReport = () => {
    window.print();
  };

  // Copy entire report to clipboard (AI-generated content only, formatted for Word)
  const copyEntireReport = async () => {
    const parts = [];
    
    // Add report header
    parts.push(`ASSESSMENT REPORT\n`);
    parts.push(`Client: ${child?.name}`);
    parts.push(`Assessment Type: ${assessment?.assessment_type}`);
    parts.push(`Date: ${assessment?.assessment_date || 'Not specified'}`);
    parts.push(`\n${'─'.repeat(50)}\n`);
    
    // Add each selected section (AI content only, not raw data)
    for (const section of REPORT_SECTIONS.filter(s => !s.parentSection && selectedSections[s.id])) {
      const scores = getScoresForSection(section);
      const interpretation = sectionInterpretations[section.id];
      
      // Skip sections without AI interpretation
      if (!interpretation && scores.length === 0) continue;
      
      parts.push(`\n${section.name.toUpperCase()}\n`);
      
      // Add scores table formatted for Word
      if (scores.length > 0) {
        parts.push('\nStandardized Assessment Results:\n');
        
        // Create a simple table format that copies well into Word
        const tableHeader = 'Test\tSubtest\tScore Type\tScore\tPercentile\tInterpretation';
        const tableDivider = '────\t────────\t──────────\t─────\t──────────\t──────────────';
        parts.push(tableHeader);
        parts.push(tableDivider);
        
        scores.forEach(score => {
          const scoreType = score.score_type || 'standard';
          const displayScore = score.converted_score ?? score.standard_score ?? score.scaled_score;
          const interpretationText = getScoreInterpretation(displayScore, scoreType);
          const scoreLabel = scoreType === 'scaled' ? 'Scaled' : 'Standard';
          
          const row = [
            score.test_code || '-',
            score.subtest_name || 'Total',
            scoreLabel,
            displayScore || '-',
            score.percentile_rank ? `${score.percentile_rank}%` : '-',
            interpretationText
          ].join('\t');
          parts.push(row);
        });
        parts.push('');
      }
      
      // Add AI interpretation (this is the key clinical content)
      if (interpretation) {
        parts.push('\n' + interpretation);
      }
      
      parts.push(`\n${'─'.repeat(50)}`);
    }
    
    try {
      const reportText = parts.join('\n');
      
      // Try to copy with rich text formatting for better Word compatibility
      if (navigator.clipboard.write) {
        try {
          // Create HTML version for rich formatting
          const htmlContent = generateHTMLReport();
          const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
          const textBlob = new Blob([reportText], { type: 'text/plain' });
          
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': htmlBlob,
              'text/plain': textBlob
            })
          ]);
          toast.success('Report copied! Paste into Word for best formatting.');
        } catch (htmlErr) {
          // Fallback to plain text
          await navigator.clipboard.writeText(reportText);
          toast.success('Report copied to clipboard!');
        }
      } else {
        await navigator.clipboard.writeText(reportText);
        toast.success('Report copied to clipboard!');
      }
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  // Generate HTML version of report for Word compatibility
  const generateHTMLReport = () => {
    // Build letterhead section
    let letterheadHTML = '';
    if (letterhead?.has_letterhead || letterhead?.practice_name) {
      letterheadHTML = '<div class="letterhead" style="text-align: center; margin-bottom: 24pt; border-bottom: 2px solid #333; padding-bottom: 16pt;">';
      
      if (letterhead?.letterhead_image) {
        letterheadHTML += `<img src="${letterhead.letterhead_image}" alt="Letterhead" style="max-height: 80px; margin-bottom: 8pt;" />`;
      }
      
      if (letterhead?.practice_name) {
        letterheadHTML += `<h2 style="margin: 0; font-size: 14pt; border-bottom: none;">${letterhead.practice_name}</h2>`;
      }
      
      if (letterhead?.practice_address) {
        letterheadHTML += `<p style="margin: 4pt 0; font-size: 10pt; color: #555;">${letterhead.practice_address.replace(/\n/g, '<br>')}</p>`;
      }
      
      const contactInfo = [letterhead?.practice_phone, letterhead?.practice_email].filter(Boolean).join(' | ');
      if (contactInfo) {
        letterheadHTML += `<p style="margin: 4pt 0; font-size: 10pt; color: #555;">${contactInfo}</p>`;
      }
      
      if (letterhead?.registration_number) {
        letterheadHTML += `<p style="margin: 4pt 0; font-size: 9pt; color: #777;">${letterhead.registration_number}</p>`;
      }
      
      letterheadHTML += '</div>';
    }

    let html = `
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; font-size: 11pt; line-height: 1.5; }
          h1 { font-size: 16pt; margin-bottom: 10pt; }
          h2 { font-size: 14pt; margin-top: 16pt; margin-bottom: 8pt; border-bottom: 1px solid #333; }
          table { border-collapse: collapse; width: 100%; margin: 10pt 0; }
          th, td { border: 1px solid #666; padding: 6pt 8pt; text-align: left; font-size: 10pt; }
          th { background-color: #f0f0f0; font-weight: bold; }
          p { margin: 8pt 0; }
          .header-info { margin-bottom: 16pt; }
          .header-info p { margin: 4pt 0; }
        </style>
      </head>
      <body>
        ${letterheadHTML}
        <h1>ASSESSMENT REPORT</h1>
        <div class="header-info">
          <p><strong>Client:</strong> ${child?.name || ''}</p>
          <p><strong>Assessment Type:</strong> ${assessment?.assessment_type || ''}</p>
          <p><strong>Date:</strong> ${assessment?.assessment_date || 'Not specified'}</p>
        </div>
    `;

    // Add each selected section
    for (const section of REPORT_SECTIONS.filter(s => !s.parentSection && selectedSections[s.id])) {
      const scores = getScoresForSection(section);
      const interpretation = sectionInterpretations[section.id];
      
      if (!interpretation && scores.length === 0) continue;
      
      html += `<h2>${section.name}</h2>`;
      
      // Add scores table
      if (scores.length > 0) {
        html += `
          <table>
            <thead>
              <tr>
                <th>Test</th>
                <th>Subtest</th>
                <th>Score Type</th>
                <th>Score</th>
                <th>Percentile</th>
                <th>Interpretation</th>
              </tr>
            </thead>
            <tbody>
        `;
        
        scores.forEach(score => {
          const scoreType = score.score_type || 'standard';
          const displayScore = score.converted_score ?? score.standard_score ?? score.scaled_score;
          const interpretationText = getScoreInterpretation(displayScore, scoreType);
          const scoreLabel = scoreType === 'scaled' ? 'Scaled' : 'Standard';
          
          html += `
            <tr>
              <td>${score.test_code || '-'}</td>
              <td>${score.subtest_name || 'Total'}</td>
              <td>${scoreLabel}</td>
              <td>${displayScore || '-'}</td>
              <td>${score.percentile_rank ? `${score.percentile_rank}%` : '-'}</td>
              <td>${interpretationText}</td>
            </tr>
          `;
        });
        
        html += `</tbody></table>`;
      }
      
      // Add AI interpretation
      if (interpretation) {
        // Convert markdown-style formatting to HTML paragraphs
        const paragraphs = interpretation.split('\n\n').filter(p => p.trim());
        paragraphs.forEach(p => {
          // Handle GLP stage tables if present
          if (p.includes('|') && p.includes('GLP Stage')) {
            html += convertMarkdownTableToHTML(p);
          } else {
            html += `<p>${p.replace(/\n/g, '<br>')}</p>`;
          }
        });
      }
    }

    html += `</body></html>`;
    return html;
  };

  // Convert markdown table to HTML table
  const convertMarkdownTableToHTML = (mdTable) => {
    const lines = mdTable.split('\n').filter(line => line.trim() && !line.match(/^[\-\|]+$/));
    if (lines.length < 2) return `<p>${mdTable}</p>`;
    
    let html = '<table><thead><tr>';
    const headers = lines[0].split('|').filter(cell => cell.trim());
    headers.forEach(header => {
      html += `<th>${header.trim()}</th>`;
    });
    html += '</tr></thead><tbody>';
    
    for (let i = 1; i < lines.length; i++) {
      const cells = lines[i].split('|').filter(cell => cell.trim());
      if (cells.length > 0) {
        html += '<tr>';
        cells.forEach(cell => {
          html += `<td>${cell.trim()}</td>`;
        });
        html += '</tr>';
      }
    }
    
    html += '</tbody></table>';
    return html;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-stone-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-stone-100">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10 print:hidden">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(`/assessments/${childId}`)}
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-serif text-primary-900">Assessment Report</h1>
              <p className="text-sm text-stone-500">{child?.name} - {assessment?.assessment_type}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {assessment?.tracker_id && (
              <Button
                variant="outline"
                onClick={syncFromTracker}
                disabled={syncingScores}
                className="rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="sync-from-tracker-btn"
                title="Re-pull the latest scores from the linked Assessment Tracker. Existing manual edits are preserved."
              >
                {syncingScores ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Syncing…</>
                ) : (
                  <><RefreshCw className="w-4 h-4 mr-2" /> Sync scores from tracker</>
                )}
              </Button>
            )}
            <Button variant="outline" onClick={copyEntireReport} className="rounded-full">
              <Copy className="w-4 h-4 mr-2" /> Copy All
            </Button>
            <Button variant="outline" onClick={exportReport} className="rounded-full">
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
            <Button variant="ghost" onClick={onLogout} className="text-stone-500">
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Language Learner Profile Banner */}
        {assessment?.language_learner_profile && (
          <Card className="p-4 mb-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200 rounded-2xl">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Brain className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-medium text-purple-900">Language Learner Profile:</h3>
                  <Badge className="bg-purple-600 text-white" data-testid="language-profile-badge">
                    {assessment.language_learner_profile === 'GLP' && 'Gestalt Language Processor'}
                    {assessment.language_learner_profile === 'Analytical' && 'Analytical Language Learner'}
                    {assessment.language_learner_profile === 'Mixed' && 'Mixed Profile'}
                    {assessment.language_learner_profile === 'AAC' && 'AAC User'}
                    {assessment.language_learner_profile === 'Other' && 'Custom Profile'}
                  </Badge>
                </div>
                <p className="text-sm text-purple-700 mt-1">
                  {assessment.language_learner_profile === 'GLP' && 'AI interpretations will recognize gestalts as meaningful communication and estimate GLP stage.'}
                  {assessment.language_learner_profile === 'Analytical' && 'AI interpretations will focus on vocabulary, syntax, and rule-based language development.'}
                  {assessment.language_learner_profile === 'Mixed' && 'AI interpretations will acknowledge both gestalt and analytical features.'}
                  {assessment.language_learner_profile === 'AAC' && 'AI interpretations will consider all communication modalities and emphasize communicative competence.'}
                  {assessment.language_learner_profile === 'Other' && (assessment.language_learner_profile_other || 'Using custom profile description.')}
                </p>
              </div>
            </div>
          </Card>
        )}
        
        {/* Warning if no profile selected */}
        {!assessment?.language_learner_profile && (
          <Card className="p-4 mb-4 bg-amber-50 border-amber-200 rounded-2xl print:hidden">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-amber-900">Language Learner Profile Not Set</h3>
                <p className="text-sm text-amber-700 mt-1">
                  For neurodiversity-affirming interpretations, please edit this assessment and select a Language Learner Profile (GLP, Analytical, Mixed, AAC, or Other).
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Info Banner */}
        <Card className="p-4 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 rounded-2xl print:hidden">
          <div className="flex items-start gap-3">
            <Brain className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">Hybrid Report Generation</h3>
              <p className="text-sm text-blue-700 mt-1">
                Your entered data is automatically formatted below. Click "Add Clinical Interpretation" on any section to add AI-powered, strengths-based clinical analysis.
              </p>
            </div>
          </div>
        </Card>

        {/* AI Disclaimer */}
        <Card className="p-4 mb-6 bg-amber-50 border-amber-200 rounded-2xl print:hidden">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <h3 className="font-medium text-amber-900">Please Review AI-Generated Content</h3>
              <p className="text-sm text-amber-700 mt-1">
                AI interpretations are designed to assist, not replace, clinical judgment. While trained on neurodiversity-affirming language, 
                <strong> errors may occur</strong>. 
                Please carefully review all AI-generated text before including it in final reports.
              </p>
            </div>
          </div>
        </Card>

        {/* Report Content */}
        <div className="space-y-4" id="report-content">
          {REPORT_SECTIONS.filter(s => !s.parentSection).map((section) => {
            const hasData = checkSectionHasData(assessment, section);
            const isSelected = selectedSections[section.id];
            const isExpanded = expandedSections[section.id] !== false;
            const autoContent = getAutoFormattedContent(section);
            const scores = getScoresForSection(section);
            const interpretation = sectionInterpretations[section.id];
            const isGenerating = generatingSection === section.id;
            
            // Get child sections (like PROMPT under Speech & Motor)
            const childSections = REPORT_SECTIONS.filter(s => s.parentSection === section.id);
            
            if (!hasData && !isSelected) return null;
            
            return (
              <Card key={section.id} className={`rounded-2xl border ${hasData ? 'border-stone-200' : 'border-dashed border-stone-300'}`}>
                {/* Section Header */}
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-stone-50 rounded-t-2xl"
                  onClick={() => toggleSection(section.id)}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox 
                      checked={isSelected}
                      onCheckedChange={(checked) => {
                        setSelectedSections(prev => ({ ...prev, [section.id]: checked }));
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="print:hidden"
                    />
                    <div>
                      <h2 className="font-semibold text-lg text-primary-900">{section.name}</h2>
                      <p className="text-sm text-stone-500">{section.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 print:hidden">
                    {/* Copy Button */}
                    {(hasData || interpretation) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          copySectionContent(section);
                        }}
                        className="rounded-full h-8 w-8 p-0"
                        title="Copy section to clipboard"
                      >
                        {copiedSection === section.id ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4 text-stone-500" />
                        )}
                      </Button>
                    )}
                    {hasData && (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        Has Data
                      </Badge>
                    )}
                    {interpretation && (
                      <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                        <Sparkles className="w-3 h-3 mr-1" /> Interpreted
                      </Badge>
                    )}
                    {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                </div>
                
                {/* Section Content */}
                {isExpanded && isSelected && (
                  <div className="p-4 pt-0 border-t border-stone-100">
                    {/* Standardized Scores Table */}
                    {scores.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-medium text-sm text-stone-700 flex items-center gap-2">
                            <Table className="w-4 h-4" /> Standardized Assessment Results
                          </h3>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyScoresTable(scores, section.id);
                            }}
                            className="rounded-full h-8 px-3 text-xs print:hidden"
                            data-testid={`copy-table-${section.id}`}
                          >
                            {copiedTable === section.id ? (
                              <>
                                <Check className="w-3 h-3 mr-1 text-green-600" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3 mr-1" />
                                Copy Table
                              </>
                            )}
                          </Button>
                        </div>
                        <div className="rounded-lg border border-stone-200 overflow-hidden overflow-x-auto">
                          {(() => {
                            // Determine which optional columns have data
                            const hasPercentile = scores.some(s => s.percentile_rank != null && s.percentile_rank !== '');
                            const hasAgeEquiv = scores.some(s => s.age_equivalent != null && s.age_equivalent !== '');
                            const hasRawScore = scores.some(s => s.raw_score != null && s.raw_score !== '');
                            
                            return (
                              <UITable>
                                <TableHeader>
                                  <TableRow className="bg-stone-50">
                                    <TableHead className="font-semibold">Test</TableHead>
                                    <TableHead className="font-semibold">Subtest</TableHead>
                                    <TableHead className="font-semibold text-center">Score Type</TableHead>
                                    <TableHead className="font-semibold text-center">Score</TableHead>
                                    {hasPercentile && <TableHead className="font-semibold text-center">Percentile</TableHead>}
                                    {hasAgeEquiv && <TableHead className="font-semibold text-center">Age Equiv.</TableHead>}
                                    {hasRawScore && <TableHead className="font-semibold text-center">Raw Score</TableHead>}
                                    <TableHead className="font-semibold text-center">Interpretation</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {scores.map((score, idx) => {
                                    const scoreType = score.score_type || 'standard';
                                    const displayScore = score.converted_score ?? score.standard_score ?? score.scaled_score;
                                    const interpretation = getScoreInterpretation(displayScore, scoreType);
                                    
                                    return (
                                      <TableRow key={idx}>
                                        <TableCell className="font-medium">{score.test_code}</TableCell>
                                        <TableCell>{score.subtest_name || '-'}</TableCell>
                                        <TableCell className="text-center">
                                          <Badge variant="outline" className="text-xs">
                                            {scoreType === 'scaled' ? 'Scaled' : 'Standard'}
                                          </Badge>
                                        </TableCell>
                                        <TableCell className="text-center font-medium">{displayScore ?? '-'}</TableCell>
                                        {hasPercentile && <TableCell className="text-center">{score.percentile_rank ?? '-'}</TableCell>}
                                        {hasAgeEquiv && <TableCell className="text-center">{score.age_equivalent ?? '-'}</TableCell>}
                                        {hasRawScore && <TableCell className="text-center">{score.raw_score ?? '-'}</TableCell>}
                                        <TableCell className="text-center">
                                          <Badge variant="outline" className={
                                            interpretation === 'Average' ? 'bg-green-50 text-green-700' :
                                            interpretation === 'Above Average' ? 'bg-blue-50 text-blue-700' :
                                            interpretation === 'Below Average' ? 'bg-amber-50 text-amber-700' :
                                            'bg-red-50 text-red-700'
                                          }>
                                            {interpretation}
                                          </Badge>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </UITable>
                            );
                          })()}
                        </div>
                        <p className="mt-2 text-xs text-stone-500">
                          Standard scores: mean=100, SD=15 (Average: 85-115) | Scaled scores: mean=10, SD=3 (Average: 7-13)
                        </p>
                        {scores.some(s => s.notes) && (
                          <div className="mt-2 text-sm text-stone-600">
                            <p className="font-medium">Notes:</p>
                            {scores.filter(s => s.notes).map((s, i) => (
                              <p key={i} className="ml-2">• {s.test_code} {s.subtest_name}: {s.notes}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Auto-formatted Content */}
                    {autoContent && (
                      <div className="mb-4">
                        <h3 className="font-medium text-sm text-stone-700 mb-2 flex items-center gap-2">
                          <FileText className="w-4 h-4" /> 
                          {section.hasLanguageSample ? 'Assessment Observations & Language Sample Analysis' : 'Assessment Observations'}
                        </h3>
                        <div className="p-4 bg-stone-50 rounded-lg text-sm text-stone-700 whitespace-pre-wrap">
                          {autoContent}
                        </div>
                        {section.hasLanguageSample && assessment?.language_sample_analysis && (
                          <p className="mt-2 text-xs text-amber-700 bg-amber-50 p-2 rounded">
                            Note: Language Sample Analysis data will be analyzed by AI according to the child's communication profile (GLP stage analysis or MLU/error analysis for analytical processors).
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Child Sections (e.g., PROMPT under Speech & Motor) */}
                    {childSections.map(childSection => {
                      const childContent = getAutoFormattedContent(childSection);
                      if (!childContent) return null;
                      
                      return (
                        <div key={childSection.id} className="mb-4 ml-4 pl-4 border-l-2 border-purple-200">
                          <h3 className="font-medium text-sm text-purple-700 mb-2">{childSection.name}</h3>
                          <div className="p-4 bg-purple-50 rounded-lg text-sm text-stone-700 whitespace-pre-wrap">
                            {childContent}
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* AI Interpretation */}
                    {interpretation && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                          <h3 className="font-medium text-sm text-purple-700 flex items-center gap-2">
                            <Sparkles className="w-4 h-4" /> Clinical Interpretation
                          </h3>
                          {/* Prominent per-section Edit + Regenerate buttons (top-right) */}
                          <div className="print:hidden flex items-center gap-2">
                            {editingSection === section.id ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={saveEditSection}
                                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 text-xs"
                                  data-testid={`save-edit-${section.id}`}
                                >
                                  <Check className="w-3.5 h-3.5 mr-1" /> Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={cancelEditSection}
                                  className="rounded-full h-8 px-3 text-xs"
                                  data-testid={`cancel-edit-${section.id}`}
                                >
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => startEditSection(section.id)}
                                  className="rounded-full border-purple-200 text-purple-700 hover:bg-purple-50 h-8 px-3 text-xs"
                                  data-testid={`edit-section-${section.id}`}
                                >
                                  <FileText className="w-3.5 h-3.5 mr-1" /> Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => generateInterpretation(section.id)}
                                  disabled={isGenerating}
                                  className="rounded-full border-purple-200 text-purple-700 hover:bg-purple-50 h-8 px-3 text-xs"
                                  data-testid={`regenerate-top-${section.id}`}
                                >
                                  {isGenerating ? (
                                    <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Regenerating…</>
                                  ) : (
                                    <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerate</>
                                  )}
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        {editingSection === section.id ? (
                          <textarea
                            value={editDraft}
                            onChange={(e) => setEditDraft(e.target.value)}
                            rows={Math.min(24, Math.max(8, (editDraft.split('\n').length || 8)))}
                            className="w-full p-4 border-2 border-purple-300 rounded-lg text-sm text-stone-700 font-sans focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white"
                            data-testid={`edit-textarea-${section.id}`}
                          />
                        ) : (
                          <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg text-sm text-stone-700 whitespace-pre-wrap border border-purple-100">
                            {interpretation}
                          </div>
                        )}
                        {feedbackSection === section.id && (
                          <div className="mt-2 print:hidden">
                            <AIFeedbackPopup type="report_interpretation" context={`Report section: ${section.name}`} onDismiss={() => setFeedbackSection(null)} />
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Add Interpretation Button (when no interpretation exists) */}
                    {!interpretation && (autoContent || scores.length > 0) && (
                      <div className="print:hidden flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          onClick={() => generateInterpretation(section.id)}
                          disabled={isGenerating}
                          className="rounded-full border-purple-200 text-purple-700 hover:bg-purple-50"
                        >
                          {isGenerating ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Add Clinical Interpretation
                            </>
                          )}
                        </Button>
                        {section.id === 'summary_recommendations' && Object.keys(sectionInterpretations || {}).some(k => k !== 'summary_recommendations' && sectionInterpretations[k]) && (
                          <Button
                            variant="outline"
                            onClick={() => generateInterpretation(section.id, { synthesiseFromPrevious: true })}
                            disabled={isGenerating}
                            className="rounded-full border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                            data-testid="auto-infer-summary-button"
                            title="Let the AI synthesise Summary & Recommendations from your already-generated sections"
                          >
                            <Sparkles className="w-4 h-4 mr-2" />
                            Auto-infer from report findings
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Auto-infer-only button when therapist hasn't entered any summary notes but has other sections */}
                    {!interpretation && !autoContent && scores.length === 0 && section.id === 'summary_recommendations' && Object.keys(sectionInterpretations || {}).some(k => k !== 'summary_recommendations' && sectionInterpretations[k]) && (
                      <div className="print:hidden">
                        <Button
                          variant="outline"
                          onClick={() => generateInterpretation(section.id, { synthesiseFromPrevious: true })}
                          disabled={isGenerating}
                          className="rounded-full border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                          data-testid="auto-infer-summary-button-empty"
                        >
                          {isGenerating ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Synthesising…
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Auto-infer Summary & Recommendations from your report
                            </>
                          )}
                        </Button>
                        <p className="text-xs text-stone-500 mt-2">
                          Skip typing this section — the AI will read your other generated sections and draft a summary, areas for growth, recommendations and goals from them.
                        </p>
                      </div>
                    )}
                    
                    {/* Regenerate Interpretation Button (when interpretation already exists) */}
                    {interpretation && (autoContent || scores.length > 0 || section.id === 'summary_recommendations') && (
                      <div className="print:hidden mt-3 pt-3 border-t border-stone-100 flex flex-wrap gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => generateInterpretation(section.id)}
                          disabled={isGenerating}
                          className="rounded-full text-purple-600 hover:bg-purple-50 hover:text-purple-700"
                          data-testid={`regenerate-interpretation-${section.id}`}
                        >
                          {isGenerating ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Regenerating...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Regenerate Interpretation
                            </>
                          )}
                        </Button>
                        {section.id === 'summary_recommendations' && Object.keys(sectionInterpretations || {}).some(k => k !== 'summary_recommendations' && sectionInterpretations[k]) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => generateInterpretation(section.id, { synthesiseFromPrevious: true })}
                            disabled={isGenerating}
                            className="rounded-full text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
                            data-testid="regenerate-auto-infer-summary"
                          >
                            <Sparkles className="w-4 h-4 mr-2" />
                            Re-synthesise from report
                          </Button>
                        )}
                        <p className="w-full text-xs text-stone-400 mt-1 ml-1">
                          Use if you&apos;ve added or changed assessment data
                        </p>
                      </div>
                    )}
                    
                    {/* No Data Message */}
                    {!autoContent && scores.length === 0 && (
                      <p className="text-sm text-stone-400 italic">No data entered for this section.</p>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
}
